import {
  normalizeSupplierMobile,
  SUPPLIER_EMAIL_REGEX,
  SUPPLIER_MOBILE_REGEX,
} from '@/utils/supplierValidation';
import type { EntityImportConfig } from '../types';

export const supplierImportConfig: EntityImportConfig = {
  entityId: 'supplier',
  title: 'Import Suppliers',
  description: 'Download a template, map columns, then validate and import suppliers.',
  doctypeLabel: 'POS Supplier',
  fields: [
    { fieldname: 'supplier_name', label: 'Supplier Name', required: true, fieldtype: 'Data' },
    {
      fieldname: 'mobile',
      label: 'Mobile',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional; must be unique per company when provided',
    },
    { fieldname: 'contact_person', label: 'Contact Person', required: false, fieldtype: 'Data' },
    { fieldname: 'email', label: 'Email', required: false, fieldtype: 'Data' },
    { fieldname: 'city', label: 'City', required: false, fieldtype: 'Link' },
    { fieldname: 'address', label: 'Address', required: false, fieldtype: 'Text' },
    {
      fieldname: 'opening_balance',
      label: 'Opening Balance',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional. Amount you already owe the supplier',
    },
  ],
  headerAliases: {
    supplier_name: ['supplier name', 'name', 'vendor', 'vendor name', 'supplier'],
    mobile: ['mobile', 'phone', 'phone number', 'contact', 'contact number', 'cellphone', 'cell'],
    contact_person: ['contact person', 'contact name', 'person', 'contactperson'],
    email: ['email', 'email address', 'e-mail'],
    city: ['city', 'city name'],
    address: ['address', 'addr', 'location'],
    opening_balance: ['opening balance', 'opening amount', 'payable', 'outstanding'],
  },
  localStorageKey: 'data-import:supplier',
  onSuccessInvalidateKeys: [['suppliers']],
  skipReasonLabels: {
    invalid_mobile: 'Invalid mobile number',
    duplicate_mobile: 'Duplicate mobile in file',
    invalid_email: 'Invalid email',
    invalid_city: 'City not found',
    already_exists: 'Supplier already exists for this company',
    manual_skip: 'Manually skipped',
    invalid_required: 'Required field missing',
  },
  normalizeRow: (raw) => {
    const mobileRaw = (raw.mobile || '').trim();
    const mobile = mobileRaw ? normalizeSupplierMobile(mobileRaw) : '';
    return {
      ...raw,
      supplier_name: (raw.supplier_name || '').trim(),
      mobile,
      contact_person: (raw.contact_person || '').trim(),
      email: (raw.email || '').trim(),
      city: (raw.city || '').trim(),
      address: (raw.address || '').trim(),
      opening_balance: (raw.opening_balance || '').trim(),
    };
  },
  clientValidateRow: (row, ctx) => {
    if (!row.supplier_name?.trim()) {
      return { reason: 'invalid_required', label: 'Supplier name is required' };
    }
    const mobile = row.mobile || '';
    if (mobile) {
      if (!SUPPLIER_MOBILE_REGEX.test(mobile)) {
        return {
          reason: 'invalid_mobile',
          label: 'Enter a valid Pakistani mobile (e.g. 03001234567)',
        };
      }
      const prev = ctx.seenMobiles.get(mobile);
      if (prev !== undefined) {
        return {
          reason: 'duplicate_mobile',
          label: `Duplicate mobile (also on row ${prev})`,
        };
      }
      ctx.seenMobiles.set(mobile, ctx.rowNumber);
    }

    if (row.email && !SUPPLIER_EMAIL_REGEX.test(row.email)) {
      return { reason: 'invalid_email', label: 'Enter a valid email address' };
    }

    if (row.city) {
      const needle = row.city.toLowerCase();
      const found = ctx.cities.some(
        (c) => c.name.toLowerCase() === needle || (c.city || '').toLowerCase() === needle
      );
      if (!found) {
        return { reason: 'invalid_city', label: `City '${row.city}' was not found` };
      }
    }

    return null;
  },
  api: {
    downloadTemplate: '/method/saas_pos.api.data_import.api.download_template',
    importRows: '/method/saas_pos.api.data_import.api.import_rows',
  },
};
